import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  FaChevronRight,
  FaUsers,
  FaCalendarAlt,
  FaUserShield,
  FaCheckCircle,
  FaHandsHelping,
  FaImages,
  FaNewspaper,
  FaBroadcastTower,
  FaSlidersH,
  FaEnvelope,
  FaSitemap,
  FaTasks,
  FaClipboardList,
  FaChartBar,
  FaProjectDiagram,
  FaRunning,
  FaTrophy,
  FaMedal,
  FaCertificate,
  FaRegFileAlt,
  FaShareAlt,
  FaEdit,
  FaSearch,
  FaBullhorn,
} from "react-icons/fa";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);

  const cards = [
    {
      title: "Manage Players & Add Player Achievements",
      link: "/admin/manage-players",
      roles: ["admin", "secretary", "district"],
      icon: FaUsers,
      category: "operations",
      description: "Player profiles, approvals and core records.",
    },
    { title: "Manage Events", link: "/admin/manage-events", roles: ["admin", "secretary", "district"], icon: FaCalendarAlt, category: "operations", description: "Create and maintain event schedules." },
    { title: "Login Management", link: "/admin/district-registration", roles: ["admin", "secretary"], icon: FaUserShield, category: "operations", description: "Control district and role-based access." },
    { title: "Event Approvals", link: "/admin/event-registrations", roles: ["admin", "secretary", "district"], icon: FaCheckCircle, category: "operations", description: "Review and approve event registrations." },
    { title: "Volunteer Approvals", link: "/admin/volunteer-registrations", roles: ["admin", "secretary", "district"], icon: FaHandsHelping, category: "operations", description: "Validate volunteer applications." },
    { title: "Manage Gallery", link: "/admin/gallery", roles: ["admin", "secretary"], icon: FaImages, category: "content", description: "Upload and organize media gallery." },
    { title: "Manage News Clips", link: "/admin/content-sections?page=news", roles: ["admin", "secretary"], icon: FaNewspaper, category: "content", description: "Publish news highlights and stories." },
    { title: "Manage Updates & Results", link: "/admin/content-sections?page=updates_results", roles: ["admin", "secretary"], icon: FaBroadcastTower, category: "content", description: "Update live feeds and result sections." },
    { title: "Manage Slider", link: "/admin/slider", roles: ["admin", "secretary"], icon: FaSlidersH, category: "content", description: "Edit homepage slider banners." },
    { title: "Mail Center", link: "/admin/mail-center", roles: ["admin", "secretary"], icon: FaEnvelope, category: "content", description: "Send announcements and manage workflow mail templates." },
    { title: "Occasion Posters", link: "/admin/occasion-posters", roles: ["admin", "secretary"], icon: FaBullhorn, category: "content", description: "Create festive poster and notification templates." },
    { title: "Manage Website Sections", link: "/admin/content-sections", roles: ["admin", "secretary"], icon: FaSitemap, category: "content", description: "Maintain structured page content." },
    { title: "Eligibility Management", link: "/admin/eligibility", roles: ["admin", "secretary"], icon: FaTasks, category: "operations", description: "Configure and review eligibility rules." },
    { title: "Score Sheet Rules", link: "/admin/score-sheet-rules", roles: ["admin", "secretary"], icon: FaClipboardList, category: "results", description: "Define score-sheet calculation logic." },
    { title: "Reports", link: "/admin/reports", roles: ["admin", "secretary"], icon: FaChartBar, category: "results", description: "View exports and summary analytics." },
    { title: "Classification Allotment", link: "/admin/classification-allotment", roles: ["admin", "secretary", "district"], icon: FaProjectDiagram, category: "operations", description: "Assign classifications to players." },
    { title: "Game Day 2", link: "/admin/game-day-2", roles: ["admin", "secretary", "district"], icon: FaRunning, category: "operations", description: "Run game-day checks and workflows." },
    { title: "Results Management", link: "/admin/results", roles: ["admin", "secretary", "district"], icon: FaTrophy, category: "results", description: "Publish and adjust result entries." },
    { title: "Player Achievements", link: "/admin/player-achievements", roles: ["admin", "secretary", "district"], icon: FaMedal, category: "results", description: "Record medals and achievements." },
    { title: "Certificates Management", link: "/admin/certificates", roles: ["admin", "secretary", "district"], icon: FaCertificate, category: "results", description: "Generate and manage certificates." },
    { title: "Issued Certificates", link: "/admin/certificates/issued", roles: ["admin", "secretary", "district"], icon: FaRegFileAlt, category: "results", description: "Track issued certificate history." },
  ];
  const accentThemes = [
    {
      header: "from-blue-500 to-cyan-500",
      badge: "bg-blue-50 text-blue-700 border-blue-200",
      hover: "hover:shadow-blue-100",
    },
    {
      header: "from-emerald-500 to-teal-500",
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
      hover: "hover:shadow-emerald-100",
    },
    {
      header: "from-amber-500 to-orange-500",
      badge: "bg-amber-50 text-amber-700 border-amber-200",
      hover: "hover:shadow-amber-100",
    },
    {
      header: "from-rose-500 to-pink-500",
      badge: "bg-rose-50 text-rose-700 border-rose-200",
      hover: "hover:shadow-rose-100",
    },
  ];

  const normalizedRole = (role || "").toLowerCase();
  const visibleCards = cards.filter((c) => c.roles.includes(normalizedRole));
  const [activeTab, setActiveTab] = useState("summary");
  const [searchQuery, setSearchQuery] = useState("");
  const roleLabel =
    normalizedRole === "admin"
      ? "Administrator"
      : normalizedRole === "secretary"
      ? "Secretary"
      : normalizedRole === "district"
      ? "District"
      : "User";
  const tabs = [
    { key: "summary", label: "Summary" },
    { key: "operations", label: "Operations" },
    { key: "content", label: "Content" },
    { key: "results", label: "Results" },
  ];
  const tabCards = useMemo(() => {
    if (activeTab === "summary") return visibleCards;
    return visibleCards.filter((card) => card.category === activeTab);
  }, [activeTab, visibleCards]);
  const filteredTabCards = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tabCards;
    return tabCards.filter((card) =>
      `${card.title} ${card.description || ""} ${card.category || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [tabCards, searchQuery]);
  const counts = useMemo(
    () => ({
      all: visibleCards.length,
      operations: visibleCards.filter((c) => c.category === "operations").length,
      content: visibleCards.filter((c) => c.category === "content").length,
      results: visibleCards.filter((c) => c.category === "results").length,
    }),
    [visibleCards]
  );
  const sparkProfiles = [
    [18, 28, 34, 46, 40, 58, 64],
    [14, 22, 20, 31, 37, 49, 60],
    [20, 36, 32, 28, 45, 55, 52],
    [10, 18, 30, 42, 39, 48, 62],
  ];
  const sparkPath = (points) =>
    points
      .map((value, index) => {
        const x = (index / Math.max(points.length - 1, 1)) * 100;
        const y = 100 - value;
        return `${x},${y}`;
      })
      .join(" ");

  const onCardKeyDown = (event, link) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigate(link);
    }
  };

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen bg-slate-100 mobile-page-pad overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-cyan-300/35 blur-3xl hidden sm:block" />
        <div className="pointer-events-none absolute top-40 -left-20 h-72 w-72 rounded-full bg-amber-300/30 blur-3xl hidden sm:block" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:28px_28px] hidden sm:block" />
        <div className="relative max-w-[1200px] mx-auto">
          <div className="px-2 sm:px-5 lg:px-6 py-2 sm:py-6">
            <section className="bg-white/85 backdrop-blur rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="border-b border-slate-200 px-3 sm:px-4 py-2.5 sm:py-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h1 className="text-base sm:text-xl font-semibold tracking-tight text-slate-900">Admin Engagement</h1>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">{roleLabel} workspace with quick actions</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-2.5 sm:px-2.5 py-1.5 sm:py-1.5 text-[11px] sm:text-xs font-semibold rounded-md bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
                  >
                    <FaShareAlt size={12} />
                    <span className="hidden sm:inline">SHARE</span>
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-2.5 sm:px-2.5 py-1.5 sm:py-1.5 text-[11px] sm:text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <FaEdit size={12} />
                    <span className="hidden sm:inline">EDIT DASHBOARD</span>
                  </button>
                </div>
              </div>

              <div className="border-b border-slate-200 px-2 sm:px-3 overflow-x-auto">
                <div className="flex min-w-max py-1">
                  {tabs.map((tab) => {
                    const active = tab.key === activeTab;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold rounded-full border transition-all mr-1.5 ${
                          active
                            ? "border-cyan-200 text-cyan-800 bg-cyan-50 shadow-sm"
                            : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="border-b border-slate-200 px-2.5 sm:px-3.5 py-2">
                <div className="relative">
                  <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search module..."
                    className="w-full h-8 sm:h-9 rounded-md border border-slate-300 bg-white pl-8 pr-2.5 text-xs sm:text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div className="p-2.5 sm:p-3.5">
                <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
                  <div className="rounded-lg border border-cyan-100 bg-cyan-50/80 px-3 py-2.5">
                    <p className="text-xs font-semibold text-cyan-800 uppercase tracking-wide">Modules</p>
                    <p className="text-xl font-semibold text-slate-900 mt-0.5">{counts.all}</p>
                  </div>
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/80 px-3 py-2.5">
                    <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Operations</p>
                    <p className="text-xl font-semibold text-slate-900 mt-0.5">{counts.operations}</p>
                  </div>
                  <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2.5">
                    <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Content</p>
                    <p className="text-xl font-semibold text-slate-900 mt-0.5">{counts.content}</p>
                  </div>
                  <div className="rounded-lg border border-rose-100 bg-rose-50/80 px-3 py-2.5">
                    <p className="text-xs font-semibold text-rose-800 uppercase tracking-wide">Results</p>
                    <p className="text-xl font-semibold text-slate-900 mt-0.5">{counts.results}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-2">
                  {filteredTabCards.map((item, idx) => {
                    const theme = accentThemes[idx % accentThemes.length];
                    const spark = sparkProfiles[idx % sparkProfiles.length];
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.title}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(item.link)}
                        onKeyDown={(event) => onCardKeyDown(event, item.link)}
                        className={`relative overflow-hidden bg-white/95 backdrop-blur border border-slate-200 rounded-md p-2 sm:p-2.5 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all ${theme.hover} focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                      >
                        <div className={`absolute -right-8 -top-8 h-14 w-14 rounded-full bg-gradient-to-br ${theme.header} opacity-15`} />
                        <div className="flex items-center justify-between">
                          <div className={`h-7 w-7 sm:h-8 sm:w-8 rounded-md border flex items-center justify-center ${theme.badge}`}>
                            <Icon size={12} />
                          </div>
                          <FaChevronRight className="text-slate-400" size={11} />
                        </div>
                        <h2 className="text-[11px] sm:text-sm font-semibold text-slate-900 mt-1.5 sm:mt-2 leading-snug min-h-[28px]">
                          {item.title}
                        </h2>
                        <p className="hidden sm:block text-xs text-slate-600 mt-1 min-h-[28px]">
                          {item.description || "Open module"}
                        </p>
                        <p className="sm:hidden text-[10px] text-slate-500 mt-0.5 uppercase tracking-wide">
                          {item.category}
                        </p>
                        <div className={`hidden sm:block mt-2 h-8 rounded-md bg-gradient-to-r ${theme.header} opacity-15`} />
                        <svg viewBox="0 0 100 40" className="hidden sm:block mt-1 w-full h-5">
                          <polyline
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            points={sparkPath(spark)}
                            className="text-slate-500"
                          />
                        </svg>
                      </div>
                    );
                  })}
                </div>
                {filteredTabCards.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-3">No modules matched your search.</p>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminDashboard;
